"use strict";

/**
 * Empresa sem licença ativa. Espelha o `isInactiveCompany` do v2, campo por campo — a mesma
 * pergunta respondida de duas formas nas duas linhas daria dois produtos diferentes.
 *
 * Só diz "inativa" quando o back afirmou que é: status ausente vira `false`. Grupos não têm
 * licença.
 *
 * Deliberadamente NÃO é `status != "ATIVO"`, que é o teste usado na carteira. Lá o pior caso de um
 * status ausente é um clique que abre o diálogo de licença; aqui é uma tela borrada por falta de
 * dado.
 *
 * Fora da entidade porque `CompanyEntity` está mockada no Jest (ver `moduleNameMapper`) — a regra
 * precisa ser verificável sem o mock.
 */
export function isInactiveCompany({ status, type, isGroup } = {}) {
    // Duas formas de dizer "grupo": o `type: "group"` do documento unificado, que é o que o back
    // grava, e o `isGroup: true` que a rota de grupo do v1 carimba no objeto em memória
    // (`group.routes.js`). Aceitar só uma delas barraria a escrita de grupo no dia em que um
    // documento de grupo ganhasse `status` — e o teste continuaria verde, porque testaria a
    // forma que este front não produz.
    if (type === "group" || isGroup === true) return false;
    return status === "INATIVO" || status === "CHURN";
}

/** Estamos dentro de uma empresa (e não de um grupo, que não tem licença). */
function isInsideCompany(path = "") {
    return /\/empresa\//.test(path);
}

/**
 * Dashboard da empresa sem licença: a tela abre, mas borrada e sem clique.
 *
 * O menu inteiro ficava trancado, e o contador precisava decidir se queria a empresa ANTES de
 * poder olhar qualquer coisa dela. Agora é o contrário: ele entra, navega em cadastro, inputs e
 * leitura consultiva, vê o dashboard embaçado e decide ali — é olhando o dado que a decisão faz
 * sentido.
 *
 * Decide pela URL e não pelo nome do estado: no `onStart` da transição o `$state` ainda é o
 * anterior — a mesma razão documentada no `getCompanyId()` da sidebar.
 */
export function isDashboardBlockedByLicense({ path = "", companyProps } = {}) {
    if (!isInsideCompany(path)) return false;
    // As superfícies que a licença paga: o que a plataforma CALCULA a partir do dado.
    //
    // - `dashboard` e `showtime`: o painel e a leitura de uma visualização dele
    // - `demonstracoes`: DRE, balanço e balancete, contábil e financeiro — o mesmo número do
    //   painel, noutra forma. Cobre também o `/showtime` aninhado deles, que fica dentro do
    //   segmento e por isso não precisa de regra própria.
    // - `plano-de-contas`: o mapeamento que decide como aquele número é montado
    //
    // Fica de fora o que é INSUMO ou LEITURA: cadastro, inputs e leitura consultiva continuam
    // abertos, porque são eles que embasam a decisão de ativar.
    //
    // Ancorado logo abaixo de `/empresa/<id>/`, e não em qualquer profundidade: casar o segmento
    // solto pegaria trechos de URL de telas vizinhas.
    if (!/\/empresa\/[^/]+\/(dashboard|showtime|demonstracoes|demonstracoes-redirect|plano-de-contas)(\/|$)/.test(path)) {
        return false;
    }
    return isInactiveCompany(companyProps);
}

/**
 * Escrita barrada por falta de licença: criar ou editar cenário, orçamento/projeção e
 * parametrização.
 *
 * Não olha a URL: a pergunta é sobre a EMPRESA em memória, e quem chama já sabe que está numa tela
 * de escrita. O bloqueio de verdade vive na API (`assertCompanyCanWrite`, no data-engine) — aqui é
 * o que evita o usuário chegar até o erro.
 */
export function isWriteBlockedByLicense({ companyProps } = {}) {
    return isInactiveCompany(companyProps);
}
